import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Link as LinkIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import type { InvitationResponse } from '@/types/invitation';
import { formatDateTime } from '@/lib/utils';

function getStatus(inv: InvitationResponse): { label: string; variant: 'default' | 'secondary' | 'destructive' } {
  if (inv.used_by) return { label: 'Used', variant: 'default' };
  if (new Date(inv.expires_at) < new Date()) return { label: 'Expired', variant: 'destructive' };
  return { label: 'Active', variant: 'secondary' };
}

interface InvitationsTableProps {
  invitations: InvitationResponse[];
}

export function InvitationsTable({ invitations }: InvitationsTableProps) {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code / Link</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Used By</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                No invitations yet.
              </TableCell>
            </TableRow>
          ) : (
            invitations.map((inv) => {
              const status = getStatus(inv);
              return (
                <TableRow key={inv.id}>
                  <TableCell>
                    <span className="font-mono text-sm">{inv.code}</span>
                    {inv.link && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground max-w-[250px]">
                        {inv.link}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(inv.created_at)}</TableCell>
                  <TableCell>{formatDateTime(inv.expires_at)}</TableCell>
                  <TableCell>
                    <Badge variant={status.variant}>{status.label}</Badge>
                  </TableCell>
                  <TableCell>{inv.used_by ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {inv.link && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(inv.link!, 'Link')}
                              />
                            }
                          >
                            <LinkIcon className="h-4 w-4" />
                          </TooltipTrigger>
                          <TooltipContent>Copy Link</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyToClipboard(inv.code, 'Code')}
                            />
                          }
                        >
                          <Copy className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>Copy Code</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
